import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

function AdminConsolePage() {
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [stories, setStories] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    const userData = localStorage.getItem('user');
    if (!userData) {
      alert('请先登录');
      window.location.href = '/login';
      return;
    }

    const user = JSON.parse(userData);
    if (user.role !== 'admin') {
      alert('需要管理员权限');
      window.location.href = '/';
      return;
    }

    fetchAllData();
  };

  const fetchAllData = async () => {
    try {
      const [overviewRes, usersRes, storiesRes] = await Promise.all([
        fetch('/api/admin/overview'),
        fetch('/api/admin/users?limit=100'),
        fetch('/api/admin/stories?limit=100')
      ]);

      const overviewData = await overviewRes.json();
      const usersData = await usersRes.json();
      const storiesData = await storiesRes.json();

      if (overviewData.overview) setOverview(overviewData.overview);
      if (usersData.users) setUsers(usersData.users);
      if (storiesData.stories) setStories(storiesData.stories);
    } catch (error) {
      console.error('获取数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePoints = async (userId, username) => {
    const points = prompt(`为用户 ${username} 调整积分:\n(正数增加，负数减少)`);
    if (!points) return;
    
    const reason = prompt('输入原因:');
    if (!reason) return;

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`/api/admin/users/${userId}/points`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ points: parseInt(points), reason })
      });

      if (response.ok) {
        alert('✅ 积分更新成功');
        fetchAllData();
      } else {
        const data = await response.json();
        alert('❌ ' + (data.error || '更新失败'));
      }
    } catch (error) {
      alert('❌ 操作失败');
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`/api/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ role: newRole })
      });

      if (response.ok) {
        alert('✅ 角色更新成功');
        fetchAllData();
      }
    } catch (error) {
      alert('❌ 操作失败');
    }
  };

  const handleBanUser = async (userId, username) => {
    const reason = prompt(`封禁用户 ${username}\n输入封禁原因:`);
    if (!reason) return;

    const token = localStorage.getItem('token');
    try {
      // 将角色设为 banned
      const response = await fetch(`/api/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ role: 'banned' })
      });

      if (response.ok) {
        alert('✅ 用户已封禁');
        fetchAllData();
      }
    } catch (error) {
      alert('❌ 操作失败');
    }
  };

  const handleDeleteStory = async (storyId, title) => {
    if (!confirm(`确定要删除故事 "${title}" 吗？此操作不可恢复！`)) return;

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`/api/admin/stories/${storyId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert('✅ 故事已删除');
        fetchAllData();
      }
    } catch (error) {
      alert('❌ 删除失败');
    }
  };

  const handleDeleteChapter = async (chapterId) => {
    if (!confirm('确定要删除此章节吗？')) return;

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`/api/chapters/${chapterId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert('✅ 章节已删除');
        fetchAllData();
      }
    } catch (error) {
      alert('❌ 删除失败');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin text-4xl mb-4">⚙️</div>
          <p className="text-gray-400">加载管理控制台...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* 顶部导航栏 */}
      <header className="bg-gray-800 border-b border-gray-700 px-6 py-4 sticky top-0 z-50">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-red-500">🛡️ 管理控制台</h1>
            <p className="text-gray-400 text-sm mt-1">系统监控与内容管理</p>
          </div>
          <div className="flex items-center space-x-4">
            <Link to="/" className="text-gray-400 hover:text-white text-sm">
              ← 返回前台
            </Link>
            <button onClick={fetchAllData} className="text-gray-400 hover:text-white">
              🔄 刷新
            </button>
          </div>
        </div>
      </header>

      {/* 标签页导航 */}
      <div className="bg-gray-800 border-b border-gray-700 px-6 sticky top-16 z-40">
        <div className="flex space-x-1 overflow-x-auto">
          <TabButton active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
            📊 数据概览
          </TabButton>
          <TabButton active={activeTab === 'users'} onClick={() => setActiveTab('users')}>
            👥 用户管理
          </TabButton>
          <TabButton active={activeTab === 'stories'} onClick={() => setActiveTab('stories')}>
            📚 内容管理
          </TabButton>
          <TabButton active={activeTab === 'reports'} onClick={() => setActiveTab('reports')}>
            ⚠️ 举报审核
          </TabButton>
          <TabButton active={activeTab === 'logs'} onClick={() => setActiveTab('logs')}>
            📝 操作日志
          </TabButton>
        </div>
      </div>

      {/* 内容区域 */}
      <main className="p-6">
        {/* 数据概览 */}
        {activeTab === 'overview' && overview && (
          <div className="space-y-6">
            {/* 统计卡片 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard title="总用户数" value={overview.stats.users} icon="👥" color="blue" />
              <StatCard title="总故事数" value={overview.stats.stories} icon="📚" color="purple" />
              <StatCard title="总章节数" value={overview.stats.chapters} icon="📝" color="green" />
              <StatCard title="总动态数" value={overview.stats.activities} icon="💬" color="red" />
            </div>

            {/* 最近注册用户 */}
            <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-700">
                <h3 className="font-bold">🆕 最近注册用户</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-700">
                    <tr>
                      <th className="text-left py-3 px-4">用户名</th>
                      <th className="text-left py-3 px-4">邮箱</th>
                      <th className="text-left py-3 px-4">注册时间</th>
                      <th className="text-left py-3 px-4">状态</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {overview.recentUsers?.slice(0, 10).map(user => (
                      <tr key={user.id} className="hover:bg-gray-700">
                        <td className="py-3 px-4">{user.username}</td>
                        <td className="py-3 px-4 text-gray-400">{user.email}</td>
                        <td className="py-3 px-4 text-gray-500 text-sm">
                          {new Date(user.created_at).toLocaleDateString('zh-CN')}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded text-xs ${
                            user.role === 'admin' ? 'bg-red-900 text-red-300' :
                            user.role === 'banned' ? 'bg-gray-600 text-gray-300' :
                            'bg-green-900 text-green-300'
                          }`}>
                            {user.role === 'admin' ? '管理员' :
                             user.role === 'banned' ? '已封禁' : '正常'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 系统状态 */}
            <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
              <h3 className="font-bold mb-4">🖥️ 系统状态</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-700 rounded p-3">
                  <div className="text-gray-400 text-xs">API 状态</div>
                  <div className="text-green-400 font-bold mt-1">● 正常运行</div>
                </div>
                <div className="bg-gray-700 rounded p-3">
                  <div className="text-gray-400 text-xs">数据库</div>
                  <div className="text-green-400 font-bold mt-1">● 连接正常</div>
                </div>
                <div className="bg-gray-700 rounded p-3">
                  <div className="text-gray-400 text-xs">前端版本</div>
                  <div className="text-white font-bold mt-1">v2.0.0</div>
                </div>
                <div className="bg-gray-700 rounded p-3">
                  <div className="text-gray-400 text-xs">后端版本</div>
                  <div className="text-white font-bold mt-1">v2.0.0</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 用户管理 */}
        {activeTab === 'users' && (
          <div className="space-y-4">
            {/* 搜索栏 */}
            <div className="flex justify-between items-center">
              <input
                type="text"
                placeholder="🔍 搜索用户名或邮箱..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full md:w-96 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg"
              />
            </div>

            {/* 用户列表 */}
            <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-700">
                    <tr>
                      <th className="text-left py-3 px-4">ID</th>
                      <th className="text-left py-3 px-4">用户名</th>
                      <th className="text-left py-3 px-4">邮箱</th>
                      <th className="text-left py-3 px-4">角色</th>
                      <th className="text-left py-3 px-4">操作</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {users
                      .filter(u => 
                        u.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        u.email.toLowerCase().includes(searchTerm.toLowerCase())
                      )
                      .map(user => (
                      <tr key={user.id} className="hover:bg-gray-700">
                        <td className="py-3 px-4 text-xs text-gray-500 font-mono">
                          {user.id.slice(0, 8)}...
                        </td>
                        <td className="py-3 px-4 font-medium">{user.username}</td>
                        <td className="py-3 px-4 text-gray-400">{user.email}</td>
                        <td className="py-3 px-4">
                          <select
                            value={user.role}
                            onChange={(e) => handleUpdateRole(user.id, e.target.value)}
                            className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-sm"
                          >
                            <option value="user">用户</option>
                            <option value="moderator">版主</option>
                            <option value="admin">管理员</option>
                            <option value="banned">封禁</option>
                          </select>
                        </td>
                        <td className="py-3 px-4 space-x-2">
                          <button
                            onClick={() => handleUpdatePoints(user.id, user.username)}
                            className="text-blue-400 hover:text-blue-300 text-sm"
                          >
                            💰 积分
                          </button>
                          {user.role !== 'banned' && (
                            <button
                              onClick={() => handleBanUser(user.id, user.username)}
                              className="text-red-400 hover:text-red-300 text-sm"
                            >
                              🚫 封禁
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* 内容管理 */}
        {activeTab === 'stories' && (
          <div className="space-y-4">
            {/* 搜索栏 */}
            <input
              type="text"
              placeholder="🔍 搜索故事标题..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full md:w-96 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg"
            />

            {/* 故事列表 */}
            <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-700">
                    <tr>
                      <th className="text-left py-3 px-4">标题</th>
                      <th className="text-left py-3 px-4">作者</th>
                      <th className="text-left py-3 px-4">分类</th>
                      <th className="text-left py-3 px-4">章节</th>
                      <th className="text-left py-3 px-4">数据</th>
                      <th className="text-left py-3 px-4">操作</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {stories
                      .filter(s => s.title.toLowerCase().includes(searchTerm.toLowerCase()))
                      .map(story => (
                      <tr key={story.id} className="hover:bg-gray-700">
                        <td className="py-3 px-4 max-w-xs truncate">
                          <div className="font-medium">{story.title}</div>
                          <div className="text-xs text-gray-500">{story.id.slice(0, 8)}...</div>
                        </td>
                        <td className="py-3 px-4 text-gray-400">{story.author_name}</td>
                        <td className="py-3 px-4">
                          <span className="px-2 py-1 bg-gray-700 rounded text-xs">
                            {story.category || '其他'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-gray-400">-</td>
                        <td className="py-3 px-4 text-sm text-gray-400">
                          📖 {story.views || 0} ❤️ {story.likes || 0}
                        </td>
                        <td className="py-3 px-4 space-x-2">
                          <Link
                            to={`/stories/${story.id}`}
                            className="text-blue-400 hover:text-blue-300 text-sm"
                          >
                            👁️ 查看
                          </Link>
                          <button
                            onClick={() => handleDeleteStory(story.id, story.title)}
                            className="text-red-400 hover:text-red-300 text-sm"
                          >
                            🗑️ 删除
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* 举报审核 */}
        {activeTab === 'reports' && (
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-8 text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <h3 className="text-xl font-bold mb-2">举报审核系统</h3>
            <p className="text-gray-400 mb-4">功能开发中...</p>
            <div className="text-sm text-gray-500">
              <p>即将支持：</p>
              <ul className="mt-2 space-y-1">
                <li>• 用户举报内容</li>
                <li>• 违规内容审核</li>
                <li>• 批量处理工具</li>
              </ul>
            </div>
          </div>
        )}

        {/* 操作日志 */}
        {activeTab === 'logs' && (
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-8 text-center">
            <div className="text-6xl mb-4">📝</div>
            <h3 className="text-xl font-bold mb-2">操作日志</h3>
            <p className="text-gray-400 mb-4">功能开发中...</p>
            <div className="text-sm text-gray-500">
              <p>即将记录：</p>
              <ul className="mt-2 space-y-1">
                <li>• 管理员操作记录</li>
                <li>• 用户积分变更</li>
                <li>• 内容删除记录</li>
              </ul>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

// 标签页按钮组件
function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-3 whitespace-nowrap border-b-2 transition-colors ${
        active
          ? 'border-red-500 text-red-400 font-medium'
          : 'border-transparent text-gray-400 hover:text-white hover:border-gray-600'
      }`}
    >
      {children}
    </button>
  );
}

// 统计卡片组件
function StatCard({ title, value, icon, color }) {
  const colors = {
    blue: 'from-blue-600 to-blue-800',
    purple: 'from-purple-600 to-purple-800',
    green: 'from-green-600 to-green-800',
    red: 'from-red-600 to-red-800'
  };

  return (
    <div className={`bg-gradient-to-br ${colors[color]} rounded-lg p-4`}>
      <div className="flex justify-between items-start">
        <div>
          <p className="text-white/80 text-sm">{title}</p>
          <p className="text-3xl font-bold mt-1">{value}</p>
        </div>
        <div className="text-3xl opacity-80">{icon}</div>
      </div>
    </div>
  );
}

export default AdminConsolePage;
